import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";

import { isForecastingEnabled, isTrafficAttributionEnabled } from "@/lib/features";

export type PageType = 'Performance' | 'Forecast' | 'Cash Flow' | 'Composer' | 'Traffic';

interface PageTypeSelectionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectType: (type: PageType) => void;
}

export const PAGE_TYPES_CONFIG: { type: PageType; icon: React.ReactNode; color: string; descriptionKey: string; requiresTemplates: boolean }[] = [
    {
        type: 'Traffic',
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        color: 'text-indigo-500 bg-indigo-50 dark:bg-indigo-500/10',
        descriptionKey: 'templates.pageTypes.trafficDescription',
        requiresTemplates: false
    },
    {
        type: 'Cash Flow',
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
        ),
        color: 'text-amber-500 bg-amber-50 dark:bg-amber-500/10',
        descriptionKey: 'templates.pageTypes.cashflowDescription',
        requiresTemplates: false
    },
    {
        type: 'Performance',
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
        ),
        color: 'text-blue-500 bg-blue-50 dark:bg-blue-500/10',
        descriptionKey: 'templates.pageTypes.performanceDescription',
        requiresTemplates: false
    },
    {
        type: 'Forecast',
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-7.714 2.143L11 21l-2.286-6.857L1 12l7.714-2.143L11 3z" />
            </svg>
        ),
        color: 'text-purple-500 bg-purple-50 dark:bg-purple-500/10',
        descriptionKey: 'templates.pageTypes.forecastDescription',
        requiresTemplates: false
    },
    {
        type: 'Composer',
        icon: (
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
        ),
        color: 'text-neutral-500 bg-neutral-100 dark:bg-neutral-500/10',
        descriptionKey: 'templates.pageTypes.composerDescription',
        requiresTemplates: true
    },
];

export const pageTypeRequiresTemplates = (type: PageType): boolean => {
    return PAGE_TYPES_CONFIG.find(c => c.type === type)?.requiresTemplates ?? true;
};

export const getAvailablePageTypesConfig = () => {
    return PAGE_TYPES_CONFIG.filter((config) => {
        if (config.type === 'Forecast' && !isForecastingEnabled()) return false;
        if (config.type === 'Traffic' && !isTrafficAttributionEnabled()) return false;
        return true;
    });
};

export const PageTypeSelectionModal: React.FC<PageTypeSelectionModalProps> = ({
    isOpen,
    onClose,
    onSelectType,
}) => {
    const { t } = useTranslation("common");
    const availableConfigs = getAvailablePageTypesConfig();

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-neutral-900 p-6 text-left align-middle shadow-xl transition-all border border-neutral-200 dark:border-neutral-800">


                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {availableConfigs.map((config) => (
                                        <button
                                            key={config.type}
                                            onClick={() => onSelectType(config.type)}
                                            className="group flex items-start gap-4 p-4 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:border-accent-primary dark:hover:border-accent-primary hover:bg-accent-primary/[0.02] dark:hover:bg-accent-primary/[0.05] transition-all duration-200 text-left"
                                        >
                                            <div className={`p-2.5 rounded-lg shrink-0 ${config.color} group-hover:scale-110 transition-transform duration-200`}>
                                                {config.icon}
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-neutral-900 dark:text-neutral-100 mb-0.5">
                                                    {t(`templates.pageTypes.${config.type.toLowerCase().replace(' ', '')}`, config.type)}
                                                </h4>
                                                <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                                                    {t(config.descriptionKey, "")}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
